import type { ProgressInfo, TableSubscription } from '@thinkalpha/table-client';

/**
 * The table construction state presented by the UI.
 *
 * `pending` deliberately has no numeric value: the percentage is not meaningful
 * until the table server has processed the initial key set.
 */
export type TableConstructionProgress = 'pending' | number | undefined;

export function getTableConstructionProgress(
    update: Pick<ProgressInfo, 'initialKeysetProcessed' | 'percentage'>,
    enhancedProgressEnabled: boolean,
): TableConstructionProgress {
    return enhancedProgressEnabled && !update.initialKeysetProcessed ? 'pending' : update.percentage;
}

/**
 * The rounded percentage from table-client is the presentation boundary. A
 * rounded 100% intentionally ends the progress treatment even if a small tail
 * of requested cells remains unbound.
 */
export function isTableConstructionActive(progress: TableConstructionProgress): boolean {
    return progress === 'pending' || (typeof progress === 'number' && progress < 100);
}

/**
 * Whether a progress update reports a finished table for the client's current subscription.
 * table-client's `complete` is exact: the initial key set is processed and every requested cell is
 * bound, where the rounded percentage the status icon shows reads 100 from 99.5% of cells bound.
 * `progressUpdate$` replays its last update when the client resubscribes, so an update for another
 * subscription never counts.
 */
export function isTableConstructionComplete(
    update: Pick<ProgressInfo, 'complete' | 'subscription'>,
    subscription: TableSubscription | undefined,
): boolean {
    return update.complete && update.subscription === subscription;
}
